"use client"

import { useState, useTransition } from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import {
  AlertCircle,
  CheckCircle2,
  KeyRound,
  Loader2,
  ShieldCheck,
  Trees,
} from "lucide-react"

const MIN_PASSWORD_LEN = 12

export function ResetPasswordViewClient({ token }: { token: string }) {
  const router = useRouter()
  const [pwd, setPwd] = useState("")
  const [confirm, setConfirm] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [pending, startTransition] = useTransition()

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (pwd.length < MIN_PASSWORD_LEN) {
      setError(`Le mot de passe doit faire au moins ${MIN_PASSWORD_LEN} caractères.`)
      return
    }
    if (pwd !== confirm) {
      setError("Les deux mots de passe ne correspondent pas.")
      return
    }
    startTransition(async () => {
      try {
        const res = await fetch("/api/users/reset-password", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token, password: pwd }),
          credentials: "include",
        })
        if (res.status === 400 || res.status === 401) {
          setError(
            "Lien expiré ou invalide. Demandez un nouvel email depuis « mot de passe oublié ».",
          )
          return
        }
        if (!res.ok) {
          const j = await res.json().catch(() => ({}))
          setError(
            (j?.errors?.[0]?.message as string) ??
              `Réinitialisation refusée (HTTP ${res.status}).`,
          )
          return
        }
        setDone(true)
        // Payload's reset endpoint already issued a fresh session cookie.
        // Bounce to the dashboard after a short success display.
        setTimeout(() => {
          router.push("/admin")
          router.refresh()
        }, 1200)
      } catch (err) {
        setError(
          `Connexion impossible — réessayez. (${(err as Error).message})`,
        )
      }
    })
  }

  return (
    <div className="adis-login-page__shell">
      <header className="adis-login-page__brand">
        <span className="adis-login-page__mark" aria-hidden>
          <Trees size={22} strokeWidth={1.7} />
        </span>
        <span className="adis-login-page__brand-text">
          <span className="adis-login-page__brand-title">ADIS 95</span>
          <span className="adis-login-page__brand-sub">
            Nouveau mot de passe
          </span>
        </span>
      </header>

      <h1 id="adis-reset-title" className="adis-login-page__title">
        Choisissez un nouveau mot de passe
      </h1>
      <p className="adis-login-page__lede">
        Au minimum {MIN_PASSWORD_LEN} caractères. Préférez une phrase
        mémorisable plutôt qu&apos;un mot court compliqué — c&apos;est
        ce que les attaquants modernes cassent en premier.
      </p>

      {done ? (
        <div
          role="status"
          className="adis-login-page__error"
          style={{
            background: "rgb(22 163 74 / 0.08)",
            borderColor: "rgb(22 163 74 / 0.3)",
            color: "rgb(15 100 50)",
          }}
        >
          <CheckCircle2 size={16} aria-hidden />
          <span>
            Mot de passe mis à jour. Redirection vers le tableau de bord.
          </span>
        </div>
      ) : (
        <form
          className="adis-login-page__form"
          onSubmit={onSubmit}
          noValidate
        >
          {error ? (
            <div role="alert" className="adis-login-page__error">
              <AlertCircle size={16} aria-hidden />
              <span>{error}</span>
            </div>
          ) : null}
          <label className="adis-login-page__field">
            <span className="adis-login-page__field-label">
              <KeyRound size={14} aria-hidden />
              Nouveau mot de passe
            </span>
            <input
              type="password"
              autoComplete="new-password"
              minLength={MIN_PASSWORD_LEN}
              required
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              className="adis-login-page__input"
              aria-required="true"
              disabled={pending}
            />
          </label>
          <label className="adis-login-page__field">
            <span className="adis-login-page__field-label">
              <KeyRound size={14} aria-hidden />
              Confirmer le mot de passe
            </span>
            <input
              type="password"
              autoComplete="new-password"
              minLength={MIN_PASSWORD_LEN}
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="adis-login-page__input"
              aria-required="true"
              disabled={pending}
            />
          </label>

          <div className="adis-login-page__actions">
            <Link href="/admin/login" className="adis-login-page__forgot">
              Annuler
            </Link>
            <button
              type="submit"
              className="adis-login-page__submit"
              disabled={pending}
            >
              {pending ? (
                <>
                  <Loader2
                    size={14}
                    aria-hidden
                    className="adis-login-page__spin"
                  />
                  Mise à jour
                </>
              ) : (
                <>
                  <ShieldCheck size={14} aria-hidden />
                  Mettre à jour
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
